import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  Button,
  TextField,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

// Predefined document types with their configurations
const DOCUMENT_TYPES = {
  NDA: {
    name: 'NDA',
    module: 'Process Document',
    subModule: 'Hr Document',
    description: 'Non-Disclosure Agreement'
  },
  APPOINTMENT_LETTER: {
    name: 'Appointment Letter',
    module: 'Process Document',
    subModule: 'Hr Document',
    description: 'Official Appointment Letter'
  },
  KRI_KPA: {
    name: 'KRI/KPA',
    module: 'Process Document',
    subModule: 'Hr Document',
    description: 'Key Result Indicators/Key Performance Areas'
  }
};

const EmployeeUpload = ({ 
  selectedEmployee, 
  onStepComplete, 
  onStepIncomplete, 
  isCompleted,
  employeeProgress 
}) => {
  const { user, tokenManager } = useAuth();
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [documentToDeleteName, setDocumentToDeleteName] = useState('');

  // Fetch documents for the selected employee
  useEffect(() => {
    if (selectedEmployee?.userId) {
      fetchDocuments();
    }
  }, [selectedEmployee?.userId]);

  if (!selectedEmployee) {
    return (
      <Alert severity="info">
        Please select an employee first to manage their document upload.
      </Alert>
    );
  }

  const fetchDocuments = async () => {
    if (!selectedEmployee?.userId) return;

    setLoadingDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Hr Document');
      queryParams.append('userId', selectedEmployee.userId);

      const response = await fetch(`${API_URL}/documents?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentTypeChange = (event) => {
    setSelectedDocumentType(event.target.value);
    setError('');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (selectedFile.size > maxSize) {
        setError('File size too large. Maximum file size is 10MB.');
        setFile(null);
        return;
      }

      // Check file type - only allow PDF files
      if (selectedFile.type !== 'application/pdf') {
        setError('Only PDF files are allowed.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!selectedDocumentType) {
      setError('Please select a document type');
      return;
    }

    if (!selectedEmployee?.userId) {
      setError('No user account linked to this employee');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const documentConfig = DOCUMENT_TYPES[selectedDocumentType];
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('module', documentConfig.module);
    uploadData.append('subModule', documentConfig.subModule);
    uploadData.append('documentName', documentConfig.name);
    uploadData.append('uploadedBy', user.email);
    uploadData.append('userId', selectedEmployee.userId);

    try {
      const response = await fetch(`${API_URL}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
        body: uploadData,
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(`${documentConfig.name} uploaded successfully!`);
        setSelectedDocumentType('');
        setFile(null);
        // Refresh documents list
        fetchDocuments();
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      onStepComplete();
    } else {
      onStepIncomplete();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check if all required documents are uploaded
  const uploadedDocumentNames = documents.map(doc => doc.documentName);
  const requiredDocuments = Object.values(DOCUMENT_TYPES).map(doc => doc.name);
  const missingDocuments = requiredDocuments.filter(docName => !uploadedDocumentNames.includes(docName));

  const handleDeleteDocument = async (documentId, documentName) => {
    setDocumentToDelete(documentId);
    setDocumentToDeleteName(documentName);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/documents/${documentToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(result.message || 'Document deleted successfully!');
        setOpenDeleteDialog(false);
        setDocumentToDelete(null);
        setDocumentToDeleteName('');
        // Refresh documents list
        fetchDocuments();
      } else {
        const error = await response.json();
        setError(error.error || error.details || 'Failed to delete document');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
    setDocumentToDeleteName('');
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        setError('File not found');
        return;
      }

      // Show downloading message
      setSuccess(`Downloading ${doc.documentName || fileName}...`);

      // Fetch the file with authorization header
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else {
          setError('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Determine file extension from original filename or content type
      let fileExtension = '';
      if (fileName.includes('.')) {
        fileExtension = fileName.split('.').pop().toLowerCase();
      } else {
        // Fallback based on content type
        const contentType = response.headers.get('content-type');
        if (contentType) {
          if (contentType.includes('application/pdf')) {
            fileExtension = 'pdf';
          }
        }
      }
      
      // Get the filename from Content-Disposition header (backend formatted)
      let downloadName = doc.documentName || fileName;
      
      // Try to get the formatted filename from the response header
      const contentDisposition = response.headers.get('content-disposition');
      
      if (contentDisposition) {
        // Extract filename from Content-Disposition header
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      } else {
        // Fallback: manually construct the filename if header is not available
        if (doc.documentName && doc.uploadedBy) {
          downloadName = `${doc.documentName} (${doc.uploadedBy})`;
        }
      }
      
      // Fallback: ensure proper filename with extension if header parsing fails
      if (fileExtension && !downloadName.toLowerCase().endsWith(`.${fileExtension}`)) {
        downloadName += `.${fileExtension}`;
      }
      
      // Create a download link with unique identifier
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`; // Unique ID
      
      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        link.click();
        
        // Clean up after download starts
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500); // Increased delay for Chrome
      });

      setSuccess('Document downloaded successfully!');

    } catch (error) {
      console.error('Download error:', error);
      setError('Failed to download document');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Employee Document Upload Management
      </Typography>
      
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This step involves collecting required documents and certificates from the employee.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={selectedDocumentType}
                  onChange={handleDocumentTypeChange}
                  label="Document Type"
                >
                  {Object.entries(DOCUMENT_TYPES).map(([key, config]) => (
                    <MenuItem key={key} value={key}>
                      <Box>
                        <Typography variant="body1">{config.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {config.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  border: '2px dashed',
                  borderColor: file ? 'success.main' : 'grey.300',
                  backgroundColor: file ? 'success.50' : 'grey.50',
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'primary.50',
                  },
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept=".pdf"
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: file ? 'success.main' : 'grey.500', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {file ? file.name : 'Click to select file'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Supported formats: PDF only (Max: 10MB)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !file || !selectedDocumentType || !selectedEmployee?.userId}
                startIcon={loading ? <CircularProgress size={20} /> : null}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                {loading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Document Status Overview */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Document Status
          </Typography>
          <Grid container spacing={2}>
            {Object.values(DOCUMENT_TYPES).map((docConfig) => {
              const isUploaded = uploadedDocumentNames.includes(docConfig.name);
              return (
                <Grid item xs={12} sm={6} md={4} key={docConfig.name}>
                  <Paper
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: isUploaded ? 'success.main' : 'warning.main',
                      backgroundColor: isUploaded ? 'success.50' : 'warning.50',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {docConfig.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {docConfig.description}
                        </Typography>
                      </Box>
                      <Chip
                        label={isUploaded ? 'Uploaded' : 'Pending'}
                        color={isUploaded ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Uploaded Documents List */}
        {selectedEmployee?.userId && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Uploaded Documents
            </Typography>
            
            {loadingDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            ) : documents.length > 0 ? (
              <Grid container spacing={3}>
                {documents.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.documentId}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" component="div">
                          {doc.documentName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {doc.fileName} • {new Date(doc.uploadedAt).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(doc)}
                          size="small"
                          color="primary"
                        >
                          Download
                        </Button>
                        <Button
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteDocument(doc.documentId, doc.documentName)}
                          size="small"
                          color="error"
                          disabled={loading && documentToDelete === doc.documentId}
                        >
                          {loading && documentToDelete === doc.documentId ? (
                            <CircularProgress size={16} />
                          ) : (
                            'Delete'
                          )}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No documents uploaded yet for this employee.
              </Alert>
            )}
          </Box>
        )}

        {/* Manual completion checkbox */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCompleted}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            label="Mark as Complete"
          />
          {missingDocuments.length > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ ml: 2 }}>
              Missing documents: {missingDocuments.join(', ')}
            </Typography>
          )}
        </Box>
      </Paper>

      {!selectedEmployee?.userId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No user account linked to this employee. Documents cannot be uploaded.
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Document Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{documentToDeleteName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The document will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteDocument} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeUpload; 